/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { extractRelativePath } from "components/Shared/DomainSplit";
import Tabs, { TabOption } from "components/Shared/dga-tabs/tabs";
import moment from "moment";
import "moment/locale/ar-ly";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import {
  TimelineTab,
  TimelineTableRow,
  TimelineWithTabsAndTableFields,
} from "./Helpers/TimelineWithTabsAndTableTypes";

type TimelineWithTabsAndTableProps = {
  SectionId: string;
  fields: TimelineWithTabsAndTableFields;
  locale: string;
};

const EMPTY_DATE = "0001-01-01T00:00:00Z";

const isValidDate = (value: string) =>
  Boolean(value) && value !== EMPTY_DATE && moment.utc(value).isValid();

const formatSitecoreTime = (value: string, locale: string) => {
  const momentLocale = locale === "ar" ? "ar-ly" : locale || "en";
  return moment.utc(value).locale(momentLocale).format("h:mm A");
};

const formatTimeRange = (startTime: string, endTime: string, locale: string) => {
  const start = isValidDate(startTime) ? formatSitecoreTime(startTime, locale) : "";
  const end = isValidDate(endTime) ? formatSitecoreTime(endTime, locale) : "";

  if (start && end) return `${start}-${end}`;
  return start || end;
};

const hasSpeakersInRows = (rows: TimelineTableRow[]) =>
  rows.some((row) => Boolean(row?.fields?.Speakers?.value?.trim()));

const getTabLabel = (tab: TimelineTab) =>
  tab.fields.TabTitle.value || tab.displayName || tab.name || "";

const TimelineWithTabsAndTable = (props: TimelineWithTabsAndTableProps): JSX.Element | null => {
  const tabs = props?.fields?.Tabs ?? [];
  const tabOptions: TabOption[] = useMemo(
    () =>
      tabs
        .map((tab) => ({
          label: getTabLabel(tab),
          value: tab.id,
        }))
        .filter((tab) => tab.label),
    [tabs]
  );
  const [selectedTabId, setSelectedTabId] = useState<string>(tabOptions[0]?.value ?? "");

  useEffect(() => {
    if (tabOptions.length) {
      setSelectedTabId(tabOptions[0].value);
    }
  }, [tabOptions]);

  if (!props?.fields?.Title?.value && tabs.length === 0) {
    return null;
  }

  const selectedTab = tabs.find((tab) => tab.id === selectedTabId) ?? tabs[0];
  const rows = selectedTab?.fields?.TabTableRows ?? [];
  const showSpeakersColumn = hasSpeakersInRows(rows);
  const cmsSectionBg = props?.fields?.SectionBackgroundColor?.value?.trim();
  const headerCellClass = "p-4 text-xs font-medium text-neutral-700 text-start";

  return (
    <div
      id={props?.SectionId}
      className={`py-10 ${props?.locale === "ar" ? "rtl" : ""}`}
      style={cmsSectionBg ? { backgroundColor: cmsSectionBg } : undefined}
    >
      <div className="responsive flex flex-col gap-8">
        {props?.fields?.Title?.value && (
          <h2 className="text-[30px] font-bold text-primary-1000">{props.fields.Title.value}</h2>
        )}

        {tabs.length > 0 && (
          <>
            {tabOptions.length > 0 && (
              <Tabs
                tabs={tabOptions}
                selectedTab={selectedTabId}
                onTabChange={setSelectedTabId}
              />
            )}

            {rows.length > 0 && (
              <div className="overflow-x-auto bg-transparent border-t border-b border-neutral-300">
                <table
                  dir={props.locale === "ar" ? "rtl" : "ltr"}
                  className="w-full border-collapse text-start bg-transparent"
                >
                  <thead>
                    <tr className="bg-neutral-100">
                      <th className={headerCellClass}>
                        {props.fields.TitleHeaderLabel?.value}
                      </th>
                      {showSpeakersColumn && (
                        <th className={headerCellClass}>
                          {props.fields.SpeakersHeaderLabel?.value}
                        </th>
                      )}
                      <th className={headerCellClass}>
                        {props.fields.DescriptionHeaderLabel?.value}
                      </th>
                      <th className={headerCellClass}>
                        {props.fields.TimeHeaderLabel?.value}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <TimelineTableRowItem
                        key={row.id}
                        row={row}
                        locale={props.locale}
                        showSpeakersColumn={showSpeakersColumn}
                        speakersIcon={props.fields.SpeakersCellIcon}
                        timeIcon={props.fields.TimeCellIcon}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

type TimelineTableRowItemProps = {
  row: TimelineTableRow;
  locale: string;
  showSpeakersColumn: boolean;
  speakersIcon: TimelineWithTabsAndTableFields["SpeakersCellIcon"];
  timeIcon: TimelineWithTabsAndTableFields["TimeCellIcon"];
};

const TimelineTableRowItem = ({
  row,
  locale,
  showSpeakersColumn,
  speakersIcon,
  timeIcon,
}: TimelineTableRowItemProps) => {
  const { Title, Speakers, Description, StartTime, EndTime } = row.fields;
  const timeRange = formatTimeRange(StartTime?.value, EndTime?.value, locale);
  const description = Description?.value?.trim() || "-";

  const cellClass = "px-4 py-5 text-base text-primary-1000 align-top";

  return (
    <tr className="border-t border-neutral-300 bg-transparent">
      <td className={cellClass}>{Title?.value}</td>
      {showSpeakersColumn && (
        <td className={cellClass}>
          <span className="inline-flex items-center gap-2">
            {speakersIcon?.value?.src && (
              <Image
                src={extractRelativePath(speakersIcon.value.src)}
                alt={speakersIcon.value.alt || ""}
                width={24}
                height={24}
                className="shrink-0 size-6 rounded-full"
              />
            )}
            {Speakers?.value}
          </span>
        </td>
      )}
      <td className={cellClass}>{description}</td>
      <td className={`${cellClass} whitespace-nowrap`}>
        {timeRange ? (
          <span className="inline-flex items-center gap-1">
            {timeIcon?.value?.src && (
              <Image
                src={extractRelativePath(timeIcon.value.src)}
                alt={timeIcon.value.alt || ""}
                width={14}
                height={14}
                className="shrink-0 size-[14px]"
              />
            )}
            <span className="!ltr font-medium text-tag-text">{timeRange}</span>
          </span>
        ) : (
          "-"
        )}
      </td>
    </tr>
  );
};

export default TimelineWithTabsAndTable;
export type { TimelineWithTabsAndTableCo } from "./Helpers/TimelineWithTabsAndTableTypes";
export type { TimelineWithTabsAndTableFields } from "./Helpers/TimelineWithTabsAndTableTypes";
